import { Button, Badge, Input } from "@aix/ui"
import { useState, useEffect } from "react"
import toast from "react-hot-toast"
import { trpc } from "../../../lib/trpc"
import dayjs from "dayjs"

interface TrackablesTabProps {
  coachId: string
}

interface TrackableValue {
  trackableId: string
  value: number | boolean | string
}

export default function TrackablesTab({ coachId }: TrackablesTabProps) {
  // Date selection - default to today
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"))

  // Trackable values state
  const [values, setValues] = useState<Record<string, number | boolean | string>>({})
  const [isDirty, setIsDirty] = useState(false)

  // Fetch trackables for this coach
  const {
    data: trackablesData,
    isLoading: trackablesLoading,
    refetch: refetchTrackables,
  } = trpc.trackables.listCoachTrackables.useQuery({
    coachId,
  })

  // Fetch existing entries for selected date
  const {
    data: existingData,
    isLoading: entriesLoading,
    refetch: refetchEntries,
  } = trpc.trackables.getEntriesByDate.useQuery({
    date: selectedDate,
  })

  // Submit mutation
  const submitMutation = trpc.trackables.submitCheckIn.useMutation({
    onSuccess: () => {
      setIsDirty(false)
      void refetchEntries()
      toast.success("Check-in saved successfully!")
    },
    onError: (error) => {
      toast.error(error.message || "Failed to save check-in")
    },
  })

  const trackables = trackablesData?.trackables || []

  // Initialize values from existing entries when date changes or data loads
  useEffect(() => {
    if (existingData?.entries) {
      const initialValues: Record<string, number | boolean | string> = {}
      existingData.entries.forEach((entry) => {
        if (entry.numericValue !== null && entry.numericValue !== undefined) {
          initialValues[entry.trackableId] = parseFloat(entry.numericValue)
        } else if (entry.booleanValue !== null && entry.booleanValue !== undefined) {
          initialValues[entry.trackableId] = entry.booleanValue
        } else if (entry.textValue !== null && entry.textValue !== undefined) {
          initialValues[entry.trackableId] = entry.textValue
        }
      })
      setValues(initialValues)
      setIsDirty(false)
    } else {
      // Reset values if no entries for this date
      setValues({})
      setIsDirty(false)
    }
  }, [existingData, selectedDate])

  const updateValue = (trackableId: string, value: number | boolean | string) => {
    setValues((prev) => ({ ...prev, [trackableId]: value }))
    setIsDirty(true)
  }

  const handleSave = () => {
    const entries: TrackableValue[] = Object.entries(values)
      .filter(([_, value]) => value !== undefined && value !== null && value !== "")
      .map(([trackableId, value]) => ({
        trackableId,
        value,
      }))

    if (entries.length === 0) {
      toast.error("Please fill in at least one trackable")
      return
    }

    submitMutation.mutate({
      date: selectedDate,
      window: "evening", // Not used when submitting all at once
      entries,
    })
  }

  const getCategoryEmoji = (category: string): string => {
    const emojiMap: Record<string, string> = {
      body: "💪",
      work: "💼",
      reflection: "🧠",
      relationship: "❤️",
      business: "📈",
    }
    return emojiMap[category] || "📌"
  }

  const renderInput = (trackable: (typeof trackables)[0]) => {
    const value = values[trackable.id]

    if (trackable.fieldType === "boolean") {
      return (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateValue(trackable.id, true)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                value === true
                  ? "bg-primary text-primary-foreground border-2 border-primary"
                  : "bg-secondary text-muted-foreground border-2 border-border hover:border-primary"
              }`}
            >
              ✅ Yes
            </button>
            <button
              onClick={() => updateValue(trackable.id, false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                value === false
                  ? "bg-destructive text-destructive-foreground border-2 border-destructive"
                  : "bg-secondary text-muted-foreground border-2 border-border hover:border-destructive"
              }`}
            >
              ❌ No
            </button>
          </div>
          {value !== undefined && (
            <Badge variant={value ? "default" : "destructive"}>{value === true ? "Yes" : "No"}</Badge>
          )}
        </div>
      )
    }

    if (trackable.fieldType === "number") {
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={value !== undefined && value !== null ? value.toString() : ""}
            onChange={(e) => {
              const num = parseFloat(e.target.value)
              updateValue(trackable.id, isNaN(num) ? "" : num)
            }}
            placeholder={`Enter ${trackable.unit || "value"}`}
            className="flex-1"
          />
          {trackable.unit && <span className="text-sm font-medium text-muted-foreground">{trackable.unit}</span>}
        </div>
      )
    }

    // text type
    return (
      <textarea
        value={value !== undefined && value !== null ? value.toString() : ""}
        onChange={(e) => updateValue(trackable.id, e.target.value)}
        placeholder="Enter your response"
        className="w-full border border-border rounded-lg px-3 py-2 min-h-[80px] bg-background text-foreground resize-none focus:outline-hidden focus:ring-2 focus:ring-primary"
      />
    )
  }

  const isLoading = trackablesLoading || entriesLoading

  // Sort trackables with empty ones first
  const sortedTrackables = [...trackables].sort((a, b) => {
    const aValue = values[a.id]
    const bValue = values[b.id]
    const aEmpty = aValue === undefined || aValue === null || aValue === ""
    const bEmpty = bValue === undefined || bValue === null || bValue === ""

    // Sort empty trackables to the top
    if (aEmpty && !bEmpty) return -1
    if (!aEmpty && bEmpty) return 1

    // If both empty or both filled, sort by displayOrder
    return a.displayOrder - b.displayOrder
  })

  return (
    <div className="space-y-6">
      {/* Header with Date Picker */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Daily Check-In</h2>
          <p className="text-muted-foreground">Track your metrics for the selected date</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Select Date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={dayjs().format("YYYY-MM-DD")}
              className="w-[180px]"
            />
          </div>

          <Button onClick={() => setSelectedDate(dayjs().format("YYYY-MM-DD"))} variant="outline" className="mt-5">
            Today
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Showing check-in for {dayjs(selectedDate).format("MMM DD, YYYY")}
            </p>
            <p className="text-lg font-semibold text-foreground mt-1">
              {
                Object.keys(values).filter((k) => values[k] !== undefined && values[k] !== null && values[k] !== "")
                  .length
              }{" "}
              / {trackables.length} completed
            </p>
          </div>
          {isDirty && (
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300">
              Unsaved Changes
            </Badge>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading trackables...</p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && trackables.length === 0 && (
        <div className="bg-secondary rounded-lg p-12 text-center border border-border">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-foreground mb-2">No Trackables Yet</h3>
          <p className="text-muted-foreground mb-6">
            This coach doesn&apos;t have any trackables configured yet. Trackables can be added through the Settings
            tab.
          </p>
        </div>
      )}

      {/* Trackables List */}
      {!isLoading && trackables.length > 0 && (
        <div className="space-y-3">
          {sortedTrackables.map((trackable) => {
            const value = values[trackable.id]
            const isEmpty = value === undefined || value === null || value === ""
            const isRequired = (trackable as any).isRequired ?? false

            return (
              <div
                key={trackable.id}
                className={`p-4 rounded-lg border-2 transition-all ${
                  isEmpty && isRequired
                    ? "border-destructive bg-destructive/5"
                    : isEmpty
                      ? "border-yellow-500 bg-yellow-50"
                      : "border-border bg-background"
                }`}
              >
                {/* Question Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-2 flex-1">
                    <span className="text-xl">{getCategoryEmoji("business")}</span>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">
                        {trackable.question}
                        {isRequired && <span className="text-destructive ml-1">*</span>}
                      </h4>
                      {trackable.unit && trackable.fieldType === "number" && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {trackable.minValue !== undefined && `Min: ${trackable.minValue}`}
                          {trackable.maxValue !== undefined && ` Max: ${trackable.maxValue}`}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {trackable.fieldType}
                  </Badge>
                </div>

                {/* Input */}
                {renderInput(trackable)}
              </div>
            )
          })}
        </div>
      )}

      {/* Save Button */}
      {!isLoading && trackables.length > 0 && (
        <div className="sticky bottom-0 bg-background border-t-2 border-border pt-4 pb-2">
          <Button
            onClick={handleSave}
            disabled={!isDirty || submitMutation.isLoading}
            loading={submitMutation.isLoading}
            className="w-full md:w-auto"
            size="lg"
          >
            💾 Save Check-In
          </Button>
          {isDirty && (
            <p className="text-xs text-muted-foreground mt-2">
              You have unsaved changes. Click save to update your check-in.
            </p>
          )}
        </div>
      )}
    </div>
  )
}
